import { createRoute } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { useApi } from '../api/ApiProvider';
import { HttpError } from '../api/client';
import { HelperError, discoverHelper } from '../api/helper';
import { initiatePairing, submitToHelper } from '../api/pairing';
import { useHelperStore } from '../stores/helper';
import { rootRoute } from './__root';

export const pairRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pair',
  component: PairPage,
});

type PairStatus =
  | { kind: 'idle' }
  | { kind: 'discovering' }
  | { kind: 'minting' }
  | { kind: 'submitting' }
  | { kind: 'paired'; deviceName: string; pairedAt: string }
  | { kind: 'error'; message: string };

function PairPage() {
  const api = useApi();
  const credentials = useHelperStore((s) => s.credentials);
  const setCredentials = useHelperStore((s) => s.setCredentials);
  const clearCredentials = useHelperStore((s) => s.clear);
  const [status, setStatus] = useState<PairStatus>({ kind: 'idle' });

  const startPairing = useCallback(async () => {
    let step: 'discovering' | 'minting' | 'submitting' = 'discovering';
    try {
      setStatus({ kind: 'discovering' });
      // Phase 1: the helper accepts any well-formed token at /v1/discover.
      // BE-009 will mint a real backend-signed discovery token; until then we
      // pass a random nonce so the helper can log it.
      const discoveryToken = crypto.randomUUID();
      const discovery = await discoverHelper(discoveryToken);

      step = 'minting';
      setStatus({ kind: 'minting' });
      const initiated = await initiatePairing(api);

      step = 'submitting';
      setStatus({ kind: 'submitting' });
      const helperBaseUrl = `http://127.0.0.1:${discovery.port}`;
      const paired = await submitToHelper(helperBaseUrl, initiated.pairing_token);

      setCredentials({
        port: discovery.port,
        bearer: paired.bearer,
        csrfToken: paired.csrf_token,
        pairedAt: paired.paired_at,
        deviceName: paired.device_name,
      });
      setStatus({ kind: 'paired', deviceName: paired.device_name, pairedAt: paired.paired_at });
    } catch (e) {
      setStatus({ kind: 'error', message: describePairingError(step, e) });
    }
  }, [api, setCredentials]);

  return (
    <section style={{ maxWidth: 640 }}>
      <h1 style={{ marginTop: 0 }}>Pair the Desktop Helper</h1>
      <p style={{ color: '#666' }}>
        The helper runs in the background and watches the local files you choose. Pairing connects
        this browser to the helper running on this machine. Both must be running on the same device.
      </p>

      {!credentials && (
        <div
          style={{
            padding: 16,
            marginBottom: 16,
            background: '#f7f9ff',
            border: '1px solid #cfd8ff',
            borderRadius: 6,
          }}
        >
          <strong>Haven't installed the desktop helper yet?</strong>
          <p style={{ margin: '6px 0 10px 0', color: '#333' }}>
            AI Assist needs a small desktop app running on your Mac or Linux machine —
            it holds your API keys and transcribes locally. The Chrome extension by itself
            can't do anything without it.
          </p>
          <a
            href="https://getaiassist.app/"
            target="_blank"
            rel="noopener"
            style={{
              display: 'inline-block',
              padding: '8px 14px',
              background: '#111',
              color: '#fff',
              borderRadius: 6,
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            Download the desktop helper →
          </a>
        </div>
      )}

      {credentials ? (
        <div
          style={{
            padding: 16,
            background: '#f0fff4',
            border: '1px solid #b7eb8f',
            borderRadius: 6,
          }}
        >
          <strong>Already paired</strong>
          <div style={{ marginTop: 6 }}>
            Device: <code>{credentials.deviceName}</code>
          </div>
          <div>
            Port: <code>{credentials.port}</code>
          </div>
          <div>
            Paired at: <code>{credentials.pairedAt}</code>
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => startPairing()}>
              Re-pair
            </button>
            <button type="button" onClick={() => clearCredentials()}>
              Forget on this browser
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => startPairing()}>
          Pair helper
        </button>
      )}

      <div style={{ marginTop: 16 }}>
        {status.kind === 'discovering' && <p>Discovering helper on 127.0.0.1:7831…</p>}
        {status.kind === 'minting' && <p>Requesting pairing token from backend…</p>}
        {status.kind === 'submitting' && <p>Submitting pairing token to helper…</p>}
        {status.kind === 'paired' && (
          <p style={{ color: '#1a7f37' }}>
            Paired with <strong>{status.deviceName}</strong> at <code>{status.pairedAt}</code>.
          </p>
        )}
        {status.kind === 'error' && status.message === 'HELPER_NOT_INSTALLED' && (
          <div
            style={{
              padding: 16,
              marginTop: 12,
              background: '#fff7ed',
              border: '1px solid #fed7aa',
              borderRadius: 6,
            }}
          >
            <strong style={{ color: '#9a3412' }}>Desktop helper not running</strong>
            <p style={{ margin: '6px 0 10px 0', color: '#333' }}>
              We couldn't reach the AI Assist desktop helper on this machine. Either it isn't
              installed yet, or it isn't launched.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <a
                href="https://getaiassist.app/"
                target="_blank"
                rel="noopener"
                style={{
                  display: 'inline-block',
                  padding: '8px 14px',
                  background: '#111',
                  color: '#fff',
                  borderRadius: 6,
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                Download for macOS / Linux →
              </a>
              <button type="button" onClick={() => startPairing()}>
                I already installed — try again
              </button>
            </div>
            <p style={{ margin: '10px 0 0 0', fontSize: 12, color: '#666' }}>
              Already installed? Make sure the AI Assist Helper icon is showing in your menu
              bar (top-right on macOS). If not, launch it from Applications.
            </p>
          </div>
        )}
        {status.kind === 'error' && status.message !== 'HELPER_NOT_INSTALLED' && (
          <p style={{ color: 'crimson' }}>Pairing failed: {status.message}</p>
        )}
      </div>

      {credentials ? <ExtensionConnectPanel credentials={credentials} /> : null}
    </section>
  );
}

/**
 * Map raw thrown errors during pairing to messages that point at the actual
 * thing to fix. "Failed to fetch" → which leg of the pairing failed; 401 →
 * "your dev JWT probably expired" because that's by far the most common
 * cause in dev.
 */
function describePairingError(step: 'discovering' | 'minting' | 'submitting', e: unknown): string {
  if (e instanceof HttpError) {
    // Backend-side errors (the api client throws this).
    if (e.status === 401) {
      return 'Backend rejected your session token (401 Unauthorized). Your dev JWT has probably expired — re-mint it with `pnpm --filter @aiassist/api mint-dev-token`, paste into apps/web/.env.local, restart the web dev server.';
    }
    if (e.status === 404) {
      return 'Backend has no pairing endpoint (404). Most likely DATABASE_URL is unset — pairing requires Postgres. Check apps/api/.env and restart the backend.';
    }
    if (e.status >= 500) {
      return `Backend error during pairing (${e.status}). Check the backend terminal for the stack trace.`;
    }
    return `${e.problem?.title ?? 'Backend error'} (${e.status})${e.problem?.detail ? `: ${e.problem.detail}` : ''}`;
  }
  if (e instanceof HelperError) {
    // Helper-side errors (HelperError comes from helper.ts).
    if (e.status === 502) {
      return 'Helper could not reach the backend (502 Bad Gateway). Make sure the backend is running and AIASSIST_BACKEND_URL points at it (default http://localhost:8080).';
    }
    if (e.status === 401) {
      return 'Helper rejected the pairing token. Tokens are valid for 5 minutes; re-pair.';
    }
    return `${e.problem?.title ?? 'Helper error'} (${e.status})${e.problem?.detail ? `: ${e.problem.detail}` : ''}`;
  }
  // Browser-layer fetch failures (TypeError "Failed to fetch" / "Load failed").
  const msg = e instanceof Error ? e.message : String(e);
  const isNetwork = /failed to fetch|load failed|networkerror/i.test(msg);
  if (isNetwork && step === 'discovering') {
    return 'HELPER_NOT_INSTALLED';
  }
  if (isNetwork && step === 'minting') {
    return 'Could not reach the AI Assist backend. Try again in a moment — this is usually a transient network hiccup.';
  }
  if (isNetwork && step === 'submitting') {
    return 'Lost the helper connection between discovery and pair. Click "Pair helper" again — the helper likely restarted.';
  }
  return msg;
}

function ExtensionConnectPanel({
  credentials,
}: {
  credentials: NonNullable<ReturnType<typeof useHelperStore.getState>['credentials']>;
}) {
  const [copiedAt, setCopiedAt] = useState<number | null>(null);
  const [sendStatus, setSendStatus] = useState<
    | { kind: 'idle' }
    | { kind: 'sending' }
    | { kind: 'ok' }
    | { kind: 'not-installed' }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });
  const blob = JSON.stringify(credentials, null, 2);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(blob);
      setCopiedAt(Date.now());
    } catch {
      setCopiedAt(null);
    }
  };

  /**
   * Push credentials directly to the extension via the web-bridge content
   * script. We ping first; if the extension isn't installed (or its bridge
   * isn't active) we time out and tell the user to fall back to the
   * manual copy-paste path below.
   */
  const sendToExtension = () => {
    setSendStatus({ kind: 'sending' });
    const origin = window.location.origin;
    let resolved = false;

    const onMessage = (event: MessageEvent) => {
      if (event.source !== window) return;
      const data = event.data as { type?: string; ok?: boolean; error?: string };
      if (!data || typeof data !== 'object') return;
      if (data.type === 'aiassist:extension-pong') {
        // Extension is alive; send the credentials.
        window.postMessage(
          { type: 'aiassist:set-helper-credentials', payload: credentials },
          origin,
        );
        return;
      }
      if (data.type === 'aiassist:helper-credentials-ack') {
        resolved = true;
        window.removeEventListener('message', onMessage);
        setSendStatus(
          data.ok
            ? { kind: 'ok' }
            : { kind: 'error', message: data.error ?? 'extension rejected the credentials' },
        );
      }
    };

    window.addEventListener('message', onMessage);
    window.postMessage({ type: 'aiassist:extension-ping' }, origin);

    // If we don't hear back inside 600ms, assume the extension's bridge
    // content script isn't loaded — most likely the extension isn't
    // installed, or hasn't been reloaded since you added it.
    window.setTimeout(() => {
      if (resolved) return;
      window.removeEventListener('message', onMessage);
      setSendStatus({ kind: 'not-installed' });
    }, 600);
  };

  return (
    <div
      style={{
        marginTop: 24,
        padding: 16,
        border: '1px solid #e5e5e5',
        borderRadius: 6,
      }}
    >
      <h2 style={{ marginTop: 0, marginBottom: 4 }}>Connect extension</h2>
      <p style={{ color: '#666', marginTop: 0 }}>
        If the AI Assist browser extension is installed, push credentials to it directly. Otherwise
        paste the JSON below into the extension's Settings page (right-click the extension icon →
        Options).
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <button type="button" onClick={sendToExtension} disabled={sendStatus.kind === 'sending'}>
          {sendStatus.kind === 'sending' ? 'Sending…' : 'Send to extension'}
        </button>
        {sendStatus.kind === 'ok' && (
          <span style={{ color: '#1a7f37', fontSize: 12 }}>
            Sent — refresh your Meet/Zoom tab to pick it up.
          </span>
        )}
        {sendStatus.kind === 'not-installed' && (
          <span style={{ color: '#92400e', fontSize: 12 }}>
            Extension didn't respond. Install it (chrome://extensions → Load unpacked →
            apps/extension/dist) or fall back to copy-paste below.
          </span>
        )}
        {sendStatus.kind === 'error' && (
          <span style={{ color: 'crimson', fontSize: 12 }}>{sendStatus.message}</span>
        )}
      </div>

      <textarea
        readOnly
        value={blob}
        rows={8}
        style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}
        onFocus={(e) => e.currentTarget.select()}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
        <button type="button" onClick={() => void copy()}>
          Copy
        </button>
        {copiedAt && <span style={{ color: '#1a7f37', fontSize: 12 }}>Copied to clipboard.</span>}
      </div>
    </div>
  );
}
